interface CsvToJsonParams {
  headersParse?: { fieldInCSV: string; fieldInJson: string }[];
  separator?: string;
  rawHeader?: boolean;
}

export class CSVHelper {
  constructor(private file: File | undefined) {}

  public formatHeader(header: string): string {
    return header.replace(/[\u0300-\u036f]/g, "").trim();
  }

  public async csvToJSON({
    separator = ";",
    headersParse,
    rawHeader = false,
  }: CsvToJsonParams = {}): Promise<{ [k: string]: string }[]> {
    if (!this.file) throw new Error("File not found");

    const text = await this.file.text();
    const lines = text.split("\n").filter((line) => line.trim());

    if (lines.length === 0) return [];

    // Process header only once
    let headerLine = lines[0];
    if (headerLine.endsWith(separator)) {
      headerLine = headerLine.slice(0, -separator.length);
    }

    const headers = headerLine
      .split(separator)
      .map((header) => (rawHeader ? header : this.formatHeader(header)));

    // Create header mapping
    const headerMap = new Map<string, number>();
    headers.forEach((header, index) => {
      const parsedField = headersParse?.find((h) => h.fieldInCSV === header);
      const jsonField = parsedField?.fieldInJson || header;
      if (!headerMap.has(jsonField)) {
        headerMap.set(jsonField, index);
      }
    });

    // Process data rows
    const result: { [k: string]: string }[] = [];

    for (let i = 1; i < lines.length; i++) {
      const values = lines[i].split(separator);
      const obj: { [k: string]: string } = {};

      for (const [jsonField, csvIndex] of headerMap) {
        obj[jsonField] = values[csvIndex] || "";
      }

      result.push(obj);
    }

    return result;
  }

  public async jsonToCSV({
    json,
    headersParse,
    sep = ";",
  }: {
    json?: { [k: string]: string }[];
    headersParse?: {
      name: string;
      field?: string;
      render?: (line: { [k: string]: string }) => string;
    }[];
    sep?: string;
  }): Promise<string> {
    if (!this.file && !json) throw new Error("File or JSON data required");

    // Only call csvToJSON if json is not provided
    if (!json) {
      json = await this.csvToJSON();
    }

    if (!json || json.length === 0) return "";

    const headers: {
      name: string;
      field?: string;
      render?: (line: { [k: string]: string }) => string;
    }[] =
      headersParse ||
      Object.keys(json[0]).map((key) => ({ name: key, field: key }));

    // Build header row
    const headerRow = headers.map((h) => h.name).join(sep);

    // Build data rows
    const dataRows = json.map((row) =>
      headers
        .map(({ field, render }) =>
          render ? render(row) : row[field || ""] || ""
        )
        .join(sep)
    );

    return [headerRow, ...dataRows].join("\n");
  }

  public async getHeaders(params?: CsvToJsonParams): Promise<string[]> {
    if (!this.file) throw new Error("File not found");

    const json = await this.csvToJSON(params);

    return json.length > 0 ? Object.keys(json[0]) : [];
  }
}
